import "./witness_party.scss";

import { useState, memo, type ComponentProps, type ReactNode } from "react";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import type { Observable } from "rxjs";

import { MeetingEndedState } from "graphql_globals";
import { VideoCondenseAction } from "common/meeting/sidebar/party/actions";
import { MeetingSidebarParty, MeetingSidebarPartyInfo } from "common/meeting/sidebar/party";
import CheckIDIcon from "assets/images/check_ID.svg";
import PopoutMenuItem from "common/core/popout_menu/item";
import UserFullName from "common/user/user_full_name";
import { StandardTrack } from "common/meeting/sidebar/party/track";
import { AttestWitnessForRemoteModal } from "common/meeting/notary/add_witness_modal/attest";
import { useNetworkQualityLevel } from "common/twilio/network_quality_changed";

import AlertAVIssueAction from "./alert_av_issue_action";
import type {
  NotaryMeetingWitnessParty as Meeting,
  NotaryMeetingWitnessParty_meetingParticipants_WitnessParticipant as WitnessParticipant,
} from "./witness_party_fragment.graphql";

type Props = {
  meeting: Meeting;
  party: {
    id: string;
    participants: WitnessParticipant[];
    qualityChanged$: Observable<number> | null;
    track: ReactNode;
  };
  notaryUser: ComponentProps<typeof AttestWitnessForRemoteModal>["notaryUser"];
};

const MESSAGES = defineMessages({
  popoutButtonLabel: {
    id: "f277348a-ca65-4f19-a7d2-f859083bab6f",
    defaultMessage: "Open witness party controls",
  },
});

function WitnessParty({ notaryUser, party, meeting }: Props) {
  const { id: meetingId } = meeting;
  const { participants, qualityChanged$, id: partyId, track } = party;
  const intl = useIntl();
  const { networkQualityLevel } = useNetworkQualityLevel({
    qualityChanged$,
    meetingId,
    partyId,
    participantRole: "witness",
  });
  const [witness] = participants;
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const handleClose = () => setConfirmModalOpen(false);
  // If they don't have a realtionship, we assume this means the notary has not confirmed them yet.
  const unconfirmed = !witness.relationshipToParent;

  return (
    <>
      <MeetingSidebarParty
        track={(trackProps) => (
          <StandardTrack
            isCompleted={meeting.endedState !== MeetingEndedState.NOT_COMPLETED}
            track={track}
            networkQualityLevel={networkQualityLevel}
            trackButtonAriaLabel={intl.formatMessage(MESSAGES.popoutButtonLabel)}
            popoutMenuProps={{
              children: () => (
                <>
                  {unconfirmed && (
                    <PopoutMenuItem
                      onClick={() => {
                        setConfirmModalOpen(true);
                      }}
                      backgroundImage={CheckIDIcon}
                      data-automation-id="confirm-witness"
                    >
                      <FormattedMessage
                        id="344b24de-f7f6-4d81-9382-667bcc66464b"
                        defaultMessage="Confirm Witness"
                      />
                    </PopoutMenuItem>
                  )}
                  <AlertAVIssueAction meeting={meeting} activeParticipantId={witness.id} />
                  <VideoCondenseAction
                    videoCondensed={trackProps.videoCondensed}
                    toggleVideoCondensed={trackProps.toggleVideoCondensed}
                  />
                </>
              ),
            }}
          />
        )}
      >
        <MeetingSidebarPartyInfo
          header={
            <>
              {unconfirmed && <span className="NotaryMeetingWitnessParty--Unconfirmed">!</span>}
              <UserFullName user={witness} wrap />
            </>
          }
          headerSubtext={
            <FormattedMessage id="55e81de6-96de-4d3d-b9a3-76fd1daa3782" defaultMessage="Witness" />
          }
        />
      </MeetingSidebarParty>
      {confirmModalOpen && (
        <AttestWitnessForRemoteModal
          onClose={handleClose}
          witnessInformation={witness}
          notaryUser={notaryUser}
          meetingId={meetingId}
        />
      )}
    </>
  );
}

export default memo(WitnessParty);
